import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Language, Theme } from '../Models/enums';

/**
 * Language and theme, chosen once and remembered.
 *
 * They are read back from localStorage at startup rather than defaulted,
 * because a tutor who picked English does not want Hebrew again on every
 * reload, and both are applied to <html> so CSS and the bidi algorithm see
 * them before the first paint.
 */

const LANGUAGE_KEY = 'magbit.language';
const THEME_KEY = 'magbit.theme';

function storedLanguage(): Language {
  const stored = localStorage.getItem(LANGUAGE_KEY);
  return stored === 'en' || stored === 'he' ? stored : 'he';
}

function storedTheme(): Theme {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export interface UiState {
  language: Language;
  theme: Theme;
  /** The mobile navigation drawer. */
  menuOpen: boolean;
}

const initialState: UiState = {
  language: storedLanguage(),
  theme: storedTheme(),
  menuOpen: false
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setLanguage(state, action: PayloadAction<Language>) {
      state.language = action.payload;
      localStorage.setItem(LANGUAGE_KEY, action.payload);
    },
    setTheme(state, action: PayloadAction<Theme>) {
      state.theme = action.payload;
      localStorage.setItem(THEME_KEY, action.payload);
    },
    setMenuOpen(state, action: PayloadAction<boolean>) {
      state.menuOpen = action.payload;
    }
  }
});

export const { setLanguage, setTheme, setMenuOpen } = uiSlice.actions;
export default uiSlice.reducer;
